import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import { getModerationQueue, approveJob, deleteJob } from '../api/job';
import { Job } from '../types/job';

const AdminJobsPage: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  useEffect(() => {
    getModerationQueue()
      .then(setJobs)
      .catch(() => setError('Failed to load job queue.'))
      .finally(() => setLoading(false));
  }, []);

  const handleApprove = async (jobId: number) => {
    setActionInProgress(jobId);
    try {
      await approveJob(jobId);
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch {
      setError('Failed to approve job.');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDelete = async (jobId: number) => {
    setActionInProgress(jobId);
    try {
      await deleteJob(jobId);
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch {
      setError('Failed to delete job.');
    } finally {
      setActionInProgress(null);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface-container-lowest font-sans antialiased">
      <TopAppBar />
      <main className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-1 text-secondary font-label-bold mb-6 hover:underline"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Admin
        </button>

        <h1 className="text-3xl font-bold text-on-surface mb-2">Job Review Queue</h1>
        <p className="text-on-surface-variant mb-8">Jobs awaiting moderation before going live.</p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-error-container text-on-error-container text-sm">{error}</div>
        )}

        {loading ? (
          <p className="text-on-surface-variant">Loading...</p>
        ) : jobs.length === 0 ? (
          <div className="text-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined text-5xl mb-3 block">check_circle</span>
            <p className="font-medium">Queue is clear</p>
            <p className="text-sm mt-1">No jobs are waiting for review.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map(job => (
              <div
                key={job.id}
                className="bg-surface rounded-2xl border border-outline-variant p-5 flex flex-col gap-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold text-on-surface text-lg truncate">{job.title}</h2>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-on-surface-variant">
                      {job.townName && (
                        <span className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[16px]">location_on</span>
                          {job.townName}
                        </span>
                      )}
                      {job.categoryName && (
                        <span className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[16px]">category</span>
                          {job.categoryName}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-[16px]">schedule</span>
                        {new Date(job.createdAt).toLocaleDateString('en-ZA')}
                      </span>
                    </div>
                    {(job.budgetMin > 0 || job.budgetMax > 0) && (
                      <p className="text-sm text-on-surface-variant mt-1">
                        Budget: R{job.budgetMin.toLocaleString()} – R{job.budgetMax.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <a
                    href={`/jobs/${job.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-primary hover:underline text-sm font-medium flex items-center gap-1"
                  >
                    <span className="material-symbols-outlined text-[16px]">open_in_new</span>
                    View
                  </a>
                </div>

                <p className="text-sm text-on-surface line-clamp-3">{job.description}</p>

                {confirmDelete === job.id ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-error-container text-on-error-container text-sm">
                    <p className="flex-1">Delete this job? The client will not be notified.</p>
                    <button
                      onClick={() => handleDelete(job.id)}
                      disabled={actionInProgress === job.id}
                      className="px-3 py-1.5 rounded-lg bg-error text-on-error font-medium text-xs disabled:opacity-50"
                    >
                      {actionInProgress === job.id ? 'Deleting...' : 'Confirm delete'}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="px-3 py-1.5 rounded-lg border border-outline-variant text-xs font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApprove(job.id)}
                      disabled={actionInProgress === job.id}
                      className="flex-1 bg-primary text-on-primary py-2 rounded-lg font-label-bold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      {actionInProgress === job.id ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(job.id)}
                      disabled={actionInProgress === job.id}
                      className="px-4 py-2 rounded-lg border border-error text-error font-label-bold text-sm hover:bg-error/10 transition-colors disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      <BottomNavBar />
    </div>
  );
};

export default AdminJobsPage;
